import React, { useEffect, useState, useMemo } from "react";
import { Plus, Link2, Trash2, Edit3, Image, Layers, RefreshCw, Loader2, Download, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { IBrandResponse } from "../../../types/Response/AdminResponse";
import { useLazyGetAllBrandsQuery, useUpdateBrandStatusMutation } from "../../../redux/actions/Admin/ProductAdmin";
import { DOWNLOAD_CATEGORIES } from "../../../config/apiUrlsAdmin";

const AdminBrandScreen: React.FC = () => {
    const navigate = useNavigate();

    // RTK Query Hooks API Setup
    const [triggerGetBrands, { data: brandsList, isLoading, isError, isFetching }] = useLazyGetAllBrandsQuery();
    const [updateBrandStatus, { isLoading: isStatusChanging }] = useUpdateBrandStatusMutation();
    const [activeSectionTitle, setActiveSectionTitle] = useState<string>("Brand");

    // Local Search State
    const [searchQuery, setSearchQuery] = useState<string>("");

    useEffect(() => {
        triggerGetBrands();
    }, [triggerGetBrands]);

    const handleAddNewBrand = () => {
        navigate("addnewBrand");
    };

    const handleEditBrand = (brand: IBrandResponse) => {
        // Route to the add/edit screen, passing the backend dataset context state cleanly
        navigate("addnewBrand", { state: { brand } });
    };

    const handleToggleActiveStatus = async (id: number, currentActiveState: boolean) => {
        if (isStatusChanging) return;

        try {
            // FIX: Match the structural object layout defined by your IChangeBrandStatusRequest contract!
            await updateBrandStatus({
                id,
                data: { isActive: !currentActiveState }
            }).unwrap();
        } catch (error) {
            console.error("Failed to alter visibility status flag:", error);
        }
    };

    const handleDownloadExcelGrid = async () => {
        try {
            const response = await fetch(DOWNLOAD_CATEGORIES, {
                method: "GET",
                         credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            if (!response.ok) throw new Error("Failed to download categories excel");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${activeSectionTitle || "System"}_Categories_Matrix.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Excel mapping export operation rejected:", error);
        }
    };

    // Client-side local filtering Memoization
    const filteredBrandsList = useMemo(() => {
        if (!brandsList) return [];
        if (!searchQuery.trim()) return brandsList;

        const query = searchQuery.toLowerCase().trim();
        return brandsList.filter((brand: IBrandResponse) => {
            const nameMatch = brand.name?.toLowerCase().includes(query);
            const descriptionMatch = brand.description?.toLowerCase().includes(query);
            const idMatch = brand.id.toString().includes(query);

            return nameMatch || descriptionMatch || idMatch;
        });
    }, [brandsList, searchQuery]);

    return (
        <div className="min-h-screen max-w-7xl mx-auto px-4 py-8 space-y-8 bg-gray-50/30">

            {/* --- TOP ACTION HEADER BLOCK --- */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between pb-5 border-b border-gray-200 gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                            Brand Management
                        </h1>
                        {(isFetching || isLoading) && (
                            <Loader2 className="w-4 h-4 animate-spin text-[#2F5E2E]" />
                        )}
                    </div>
                    <p className="text-xs text-gray-500 max-w-2xl">
                        Configure globally recognized vendor entities, upload associated vector logos, asset media banners, and manage system slug parameters.
                    </p>
                </div>
                <div>
                    <div className="flex items-center gap-2 pb-2">
                        <button
                            onClick={() => triggerGetBrands()}
                            disabled={isLoading || isFetching}
                            className="p-2.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition cursor-pointer disabled:opacity-40"
                        >
                            <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} />
                        </button>

                        <button
                            onClick={handleAddNewBrand}
                            className="inline-flex items-center justify-center gap-1.5 bg-[#2F5E2E] hover:bg-[#234722] text-white px-4 py-2.5 rounded-lg text-xs font-semibold tracking-wide transition-colors duration-150 shadow-sm outline-none shrink-0 cursor-pointer"
                        >
                            <Plus size={15} />
                            <span>Add New Brand</span>
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={handleDownloadExcelGrid}
                        className="inline-flex items-center justify-center gap-1.5 bg-[#2F5E2E] hover:bg-[#234722] text-white px-4 py-2.5 rounded-lg text-xs font-semibold tracking-wide transition-colors duration-150 shadow-sm outline-none shrink-0 cursor-pointer"
                    >
                        <Download className="w-3.5 h-3.5" />
                        <span>Export categories Excel</span>
                    </button>
                </div>

            </div>

            {/* --- LOCAL SEARCH CONTROL INPUT BLOCK --- */}
            {!isLoading && !isError && brandsList && (
                <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <Search size={16} />
                    </div>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search brands by name, description, or ID..."
                        className="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2F5E2E]/20 focus:border-[#2F5E2E] transition-all shadow-xs"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery("")}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition cursor-pointer"
                        >
                            <X size={15} />
                        </button>
                    )}
                </div>
            )}

            {/* --- API DATA FETCH LAYER INTERCEPTS --- */}
            {isLoading && (
                <div className="text-center py-20 text-xs text-gray-400 italic">
                    Syncing catalog brands indexes records...
                </div>
            )}

            {isError && (
                <div className="text-center py-12 text-xs font-semibold text-red-600 bg-red-50 rounded-xl border border-red-100">
                    Failed to sync source brand records array from core server data gateway.
                </div>
            )}

            {/* --- CORE ENTITY GRID LAYOUT --- */}
            {!isLoading && !isError && brandsList && (
                brandsList.length === 0 ? (
                    <div className="bg-white border border-dashed rounded-xl p-12 text-center text-xs text-gray-400">
                        No registered system partner brand configurations recorded. Click Add Brand above to initialize.
                    </div>
                ) : filteredBrandsList.length === 0 ? (
                    <div className="bg-white border border-dashed rounded-xl p-12 text-center text-xs text-gray-400">
                        No brand entities match your current search query "{searchQuery}".
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {filteredBrandsList.map((brand: IBrandResponse) => {
                            const isCurrentActive = brand.isActive !== false;

                            return (
                                <div
                                    key={brand.id}
                                    className="bg-white rounded-xl border border-gray-200 shadow-xs hover:shadow-sm transition-all duration-200 overflow-hidden flex flex-col sm:flex-row p-4 gap-5 items-start sm:items-center"
                                >
                                    {/* BRAND CANVAS IMAGERY LOGO CANVASES */}
                                    <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 overflow-hidden shadow-inner">
                                        {brand.logoUrl ? (
                                            <img
                                                src={brand.logoUrl}
                                                alt={`${brand.name} Logo`}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="text-gray-300">
                                                <Image size={24} className="stroke-1" />
                                            </div>
                                        )}
                                    </div>

                                    {/* CORE METADATA DATA RENDER FRAMEWORK */}
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <h2 className="text-base font-bold text-gray-900 tracking-tight truncate">
                                                {brand.name} (#{brand.id})
                                            </h2>
                                        </div>
                                        <p className="text-xs text-gray-500 font-normal line-clamp-2 pr-4">
                                            {brand.description || "No clear custom description paragraph logged for this business vendor entity."}
                                        </p>
                                    </div>

                                    {/* MANAGEMENT CONTROL SWITCH BUTTON ARRAYS */}
                                    <div className="flex items-center gap-3 shrink-0 self-end sm:self-center w-full sm:w-auto justify-end border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-50">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`text-[11px] font-bold ${isCurrentActive ? "text-[#2F5E2E]" : "text-gray-400"}`}>
                                                {isCurrentActive ? "Active" : "Disabled"}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleToggleActiveStatus(brand.id, brand.isActive)}
                                                className="relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out border border-transparent outline-none"
                                                style={{ backgroundColor: isCurrentActive ? "#2F5E2E" : "#CBD5E1" }}
                                            >
                                                <span
                                                    className="pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out"
                                                    style={{ transform: isCurrentActive ? "translateX(12px)" : "translateX(1px)", marginTop: "1px" }}
                                                />
                                            </button>
                                        </div>

                                        <div className="h-4 w-px bg-gray-200 mx-1 hidden sm:block" />

                                        <button
                                            onClick={() => handleEditBrand(brand)}
                                            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors cursor-pointer bg-white flex items-center gap-1 text-xs font-semibold"
                                        >
                                            <Edit3 size={13} />
                                            <span>Edit</span>
                                        </button>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                )
            )}
        </div>
    );
};

export default AdminBrandScreen;